/**
 * DescriptionList component for the Indigo design system.
 *
 * A two-column term → description list used for summary read-outs like the Case
 * Overview "At a glance" / "Claim info" block. The term is a level-3 `Heading`
 * (14px bold), the description is `Body` (14px); both use the secondary text
 * color. The term column has a fixed width; descriptions wrap and may contain
 * inline nodes (e.g. a status chip).
 *
 * @example
 * ```tsx
 * <DescriptionList
 *   items={[
 *     { term: "Claim info", description: <>Specific trauma; DOI: 09/12/2024</> },
 *     { term: "Demographics", description: "39 y/o male, warehouse worker" },
 *   ]}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, type ReactNode } from "react";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./DescriptionList.css";

export interface DescriptionListItem {
  /** Left-column term. */
  term: ReactNode;
  /** Right-column description. */
  description: ReactNode;
}

export interface DescriptionListProps {
  /** Rows to render. */
  items: DescriptionListItem[];

  /**
   * Width of the term column (any CSS length).
   * @default "160px"
   */
  termWidth?: string;

  /** Additional CSS classes applied to the list. */
  className?: string;
}

export const DescriptionList = forwardRef<HTMLDivElement, DescriptionListProps>(
  ({ items, termWidth = "160px", className }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx("indigo-description-list", className)}
        style={
          {
            "--indigo-description-list-term-width": termWidth,
          } as React.CSSProperties
        }
      >
        {items.map((item, index) => (
          <div key={index} className="indigo-description-list__row">
            <Heading
              level={3}
              className="indigo-description-list__term text-indigo-text-secondary"
            >
              {item.term}
            </Heading>
            <Body
              size="md"
              className="indigo-description-list__description text-indigo-text-secondary"
            >
              {item.description}
            </Body>
          </div>
        ))}
      </div>
    );
  },
);

DescriptionList.displayName = "DescriptionList";
